import React, { useState, useEffect, useRef } from 'react';
import { X, Save, Eye, Settings as SettingsIcon, Download, Printer } from 'lucide-react';
import QRCode from 'qrcode.react';
import { toPng } from 'html-to-image';

const DesignCustomizationModal = ({ event, onClose, onSave, initialStyles, initialVisibility }) => {
    const [elementStyles, setElementStyles] = useState(initialStyles);
    const [globalVisibility, setGlobalVisibility] = useState(initialVisibility);
    const [selectedElement, setSelectedElement] = useState(null);
    const [showVisibilityModal, setShowVisibilityModal] = useState(false);
    const [previewScale, setPreviewScale] = useState(50);
    const cardRef = useRef(null);

    // Generate dynamic elements based on event's participantFields
    const getDynamicElements = () => {
        const elements = [];

        // Add participant fields from event configuration
        if (event.participantFields) {
            event.participantFields.forEach(field => {
                const fieldKey = field.fieldName.toLowerCase().replace(/\s+/g, '');
                elements.push({
                    id: fieldKey,
                    label: field.fieldName,
                    icon: getFieldIcon(field.fieldType),
                    type: 'text', // All participant fields are text-based
                    fieldType: field.fieldType
                });
            });
        }

        // Always add these system fields
        elements.push(
            { id: 'qrCode', label: 'QR Code', icon: '📱', type: 'qrcode' },
            { id: 'participantId', label: 'Registration ID', icon: '🆔', type: 'text' }
        );

        // Add category name for free events that have participant categories
        if (event.eventType === 'free' && event.participantCategories?.length > 0) {
            elements.push(
                { id: 'categoryName', label: 'Participant Category', icon: '🏷️', type: 'text' }
            );
        }

        // Add ticket-related fields if event is paid
        if (event.eventType === 'paid') {
            elements.push(
                { id: 'ticketCategory', label: 'Ticket Category', icon: '🎫', type: 'text' },
                { id: 'ticketDates', label: 'Ticket Dates', icon: '📅', type: 'text' }
            );
        }

        return elements;
    };

    const getFieldIcon = (fieldType) => {
        const icons = {
            text: '📝',
            email: '📧',
            phone: '📞',
            number: '🔢',
            date: '📅',
            textarea: '📄',
            select: '📋',
            checkbox: '☑️',
            file: '📎'
        };
        return icons[fieldType] || '📝';
    };

    const elements = getDynamicElements();

    // Initialize selected element
    useEffect(() => {
        if (!selectedElement && elements.length > 0) {
            setSelectedElement(elements[0].id);
        }
    }, [elements]);

    // Ensure all elements have styles and visibility
    useEffect(() => {
        const newStyles = { ...elementStyles };
        const newVisibility = { ...globalVisibility };

        elements.forEach(element => {
            if (!newStyles[element.id]) {
                newStyles[element.id] = getDefaultStyleForElement(element);
            }
            if (newVisibility[element.id] === undefined) {
                newVisibility[element.id] = true;
            }
        });

        setElementStyles(newStyles);
        setGlobalVisibility(newVisibility);
    }, [event]);

    const getDefaultStyleForElement = (element) => {
        if (element.type === 'qrcode') {
            return { bottom: 215, left: "50%", size: 100 };
        }
        return {
            top: 200,
            left: "50%",
            fontSize: 16,
            color: "black",
            textAlign: "center"
        };
    };

    // Sample participant for preview
    const getSampleParticipant = () => {
        const data = {};

        // Add sample data for each participant field
        event.participantFields?.forEach(field => {
            const fieldKey = field.fieldName.toLowerCase().replace(/\s+/g, '');
            if (field.fieldName.toLowerCase().includes('name')) {
                data[field.fieldName] = 'David Padvorac';
            } else if (field.fieldName.toLowerCase().includes('email')) {
                data[field.fieldName] = 'david@example.com';
            } else if (field.fieldName.toLowerCase().includes('phone')) {
                data[field.fieldName] = '+1234567890';
            } else if (field.fieldName.toLowerCase().includes('organization') || field.fieldName.toLowerCase().includes('institute')) {
                data[field.fieldName] = 'INDICA Mysuru Yoga Utsava';
            } else if (field.fieldName.toLowerCase().includes('designation') || field.fieldName.toLowerCase().includes('title')) {
                data[field.fieldName] = 'DELEGATE';
            } else {
                data[field.fieldName] = `Sample ${field.fieldName}`;
            }
        });

        const sampleCategoryName = event.participantCategories?.[0]?.name || 'Delegate';

        return {
            _id: 'preview-123',
            registrationId: 'SAMPLE-001',
            categoryName: sampleCategoryName,
            ticketCategory: 'VIP Pass',
            ticketDates: ['2025-12-18', '2025-12-21'],
            data
        };
    };

    const sampleParticipant = getSampleParticipant();

    const updateElementStyle = (element, property, value) => {
        setElementStyles(prev => ({
            ...prev,
            [element]: {
                ...prev[element],
                [property]: value
            }
        }));
    };

    const handleSave = async () => {
        await onSave(elementStyles, globalVisibility);
        onClose();
    };

    const handleDownloadPreview = async () => {
        if (!cardRef.current) return;

        try {
            const dataUrl = await toPng(cardRef.current, {
                cacheBust: true,
                backgroundColor: null,
                quality: 1,
                pixelRatio: 3,
                skipFonts: true
            });

            const link = document.createElement('a');
            link.href = dataUrl;
            link.download = `${event.eventName}-preview-card.png`;
            link.click();
        } catch (error) {
            console.error('Error downloading preview:', error);
        }
    };

    if (!selectedElement) return null;

    const currentElement = elementStyles[selectedElement] || {};
    const selectedElementData = elements.find(e => e.id === selectedElement);
    const hasSize = selectedElementData?.type === 'qrcode';
    const hasFont = selectedElementData?.type !== 'qrcode';

    // Get background image
    const getBackground = () => {
        if (event.tickets && event.tickets[0]?.idCardBackground) {
            return event.tickets[0].idCardBackground;
        }
        return event.defaultIdCardBackground || null;
    };

    // Format ticket dates
    const formatTicketDates = (dates) => {
        if (!dates || dates.length === 0) return "";
        return dates
            .map((date) =>
                new Date(date).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short",
                })
            )
            .join(", ");
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-t-2xl">
                    <div>
                        <h2 className="text-2xl font-bold flex items-center gap-2">
                            <SettingsIcon className="w-6 h-6" />
                            ID Card Designer
                        </h2>
                        <p className="text-blue-100 text-sm mt-1">Customize your ID card elements for {event.eventName}</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <div className="flex-1 flex overflow-hidden">
                    {/* Left Panel - Element Selection */}
                    <div className="w-64 border-r bg-gray-50 overflow-y-auto">
                        <div className="p-4">
                            <h3 className="text-sm font-semibold text-gray-600 mb-3">CARD ELEMENTS</h3>
                            <div className="space-y-1">
                                {elements.map(element => (
                                    <button
                                        key={element.id}
                                        onClick={() => setSelectedElement(element.id)}
                                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${selectedElement === element.id
                                            ? 'bg-blue-600 text-white shadow-md'
                                            : 'bg-white text-gray-700 hover:bg-gray-100'
                                            }`}
                                    >
                                        <span className="text-2xl">{element.icon}</span>
                                        <span className="font-medium text-sm flex-1 text-left">{element.label}</span>
                                        {!globalVisibility[element.id] && (
                                            <span className="text-xs opacity-60">Hidden</span>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Middle Panel - Controls */}
                    <div className="flex-1 overflow-y-auto p-6 bg-white">
                        <div className="max-w-md space-y-6">
                            <div className="bg-blue-50 border-l-4 border-blue-600 p-4 rounded">
                                <p className="text-sm text-blue-800">
                                    <strong>Editing:</strong> {selectedElementData?.label}
                                </p>
                            </div>

                            {/* Position Controls */}
                            <div className="bg-gray-50 rounded-xl p-5">
                                <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                    📍 Position
                                </h3>

                                {/* Y Position */}
                                <div className="mb-4">
                                    <label className="text-sm font-medium text-gray-700 mb-2 block">
                                        Y Position (Vertical)
                                    </label>
                                    <div className="flex items-center gap-3">
                                        <input
                                            type="range"
                                            min="0"
                                            max="610"
                                            value={currentElement.top || currentElement.bottom || 0}
                                            onChange={(e) => {
                                                const prop = currentElement.top !== undefined ? 'top' : 'bottom';
                                                updateElementStyle(selectedElement, prop, parseInt(e.target.value));
                                            }}
                                            className="flex-1 h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer"
                                        />
                                        <span className="text-sm font-mono bg-white px-3 py-1 rounded border min-w-[60px] text-center">
                                            {currentElement.top || currentElement.bottom || 0}
                                        </span>
                                    </div>
                                </div>

                                {/* X Position */}
                                <div className="mb-4">
                                    <label className="text-sm font-medium text-gray-700 mb-2 block">
                                        X Position (Horizontal)
                                    </label>
                                    <div className="flex items-center gap-3 mb-2">
                                        <input
                                            type="range"
                                            min="0"
                                            max="430"
                                            value={typeof currentElement.left === 'number' ? currentElement.left : (currentElement.left === '0%' ? 0 : currentElement.left === '100%' ? 430 : 215)}
                                            onChange={(e) => {
                                                updateElementStyle(selectedElement, 'left', parseInt(e.target.value));
                                            }}
                                            className="flex-1 h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer"
                                        />
                                        <span className="text-sm font-mono bg-white px-3 py-1 rounded border min-w-[60px] text-center">
                                            {typeof currentElement.left === 'string' ?
                                                currentElement.left :
                                                (currentElement.left || 215) + 'px'}
                                        </span>
                                    </div>
                                    {/* Quick alignment buttons */}
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => {
                                                updateElementStyle(selectedElement, 'left', '0%');
                                                updateElementStyle(selectedElement, 'textAlign', 'left');
                                            }}
                                            className={`flex-1 px-2 py-1 text-xs rounded border hover:bg-gray-50 ${currentElement.left === '0%' ? 'bg-blue-100 border-blue-500 text-blue-700' : 'bg-white'}`}
                                        >
                                            Left
                                        </button>
                                        <button
                                            onClick={() => {
                                                updateElementStyle(selectedElement, 'left', '50%');
                                                updateElementStyle(selectedElement, 'textAlign', 'center');
                                            }}
                                            className={`flex-1 px-2 py-1 text-xs rounded border hover:bg-gray-50 ${currentElement.left === '50%' ? 'bg-blue-100 border-blue-500 text-blue-700' : 'bg-white'}`}
                                        >
                                            Center
                                        </button>
                                        <button
                                            onClick={() => {
                                                updateElementStyle(selectedElement, 'left', '100%');
                                                updateElementStyle(selectedElement, 'textAlign', 'right');
                                            }}
                                            className={`flex-1 px-2 py-1 text-xs rounded border hover:bg-gray-50 ${currentElement.left === '100%' ? 'bg-blue-100 border-blue-500 text-blue-700' : 'bg-white'}`}
                                        >
                                            Right
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Font Controls */}
                            {hasFont && (
                                <div className="bg-gray-50 rounded-xl p-5">
                                    <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        🔤 Typography
                                    </h3>

                                    {/* Font Size */}
                                    <div className="mb-4">
                                        <label className="text-sm font-medium text-gray-700 mb-2 block">
                                            Font Size
                                        </label>
                                        <div className="flex items-center gap-3">
                                            <button
                                                onClick={() => updateElementStyle(selectedElement, 'fontSize', Math.max(8, (currentElement.fontSize || 16) - 1))}
                                                className="px-3 py-2 bg-white border rounded hover:bg-gray-100"
                                            >
                                                -
                                            </button>
                                            <span className="text-lg font-mono font-bold min-w-[50px] text-center">
                                                {currentElement.fontSize || 16}
                                            </span>
                                            <button
                                                onClick={() => updateElementStyle(selectedElement, 'fontSize', (currentElement.fontSize || 16) + 1)}
                                                className="px-3 py-2 bg-white border rounded hover:bg-gray-100"
                                            >
                                                +
                                            </button>
                                        </div>
                                    </div>

                                    {/* Text Alignment */}
                                    <div className="mb-4">
                                        <label className="text-sm font-medium text-gray-700 mb-2 block">
                                            Text Alignment
                                        </label>
                                        <div className="flex gap-2">
                                            {['left', 'center', 'right'].map(align => (
                                                <button
                                                    key={align}
                                                    onClick={() => updateElementStyle(selectedElement, 'textAlign', align)}
                                                    className={`flex-1 px-4 py-2 rounded ${(currentElement.textAlign || 'center') === align
                                                        ? 'bg-blue-600 text-white'
                                                        : 'bg-white border hover:bg-gray-100'
                                                        }`}
                                                >
                                                    {align.charAt(0).toUpperCase() + align.slice(1)}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Color Picker */}
                                    <div>
                                        <label className="text-sm font-medium text-gray-700 mb-2 block">
                                            Text Color
                                        </label>
                                        <div className="flex items-center gap-3">
                                            <input
                                                type="color"
                                                value={currentElement.color || '#000000'}
                                                onChange={(e) => updateElementStyle(selectedElement, 'color', e.target.value)}
                                                className="w-12 h-12 rounded border cursor-pointer"
                                            />
                                            <input
                                                type="text"
                                                value={currentElement.color || '#000000'}
                                                onChange={(e) => updateElementStyle(selectedElement, 'color', e.target.value)}
                                                className="flex-1 px-3 py-2 border rounded font-mono text-sm"
                                                placeholder="#000000"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Category Background Color — only for categoryName element */}
                            {selectedElement === 'categoryName' && (
                                <div className="bg-gray-50 rounded-xl p-5">
                                    <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        🎨 Category Badge
                                    </h3>
                                    <div className="mb-4">
                                        <label className="text-sm font-medium text-gray-700 mb-2 block">
                                            Background Color
                                        </label>
                                        <div className="flex items-center gap-3">
                                            <input
                                                type="color"
                                                value={currentElement.bgColor || '#ffffff'}
                                                onChange={(e) => updateElementStyle(selectedElement, 'bgColor', e.target.value)}
                                                className="w-12 h-12 rounded border cursor-pointer"
                                            />
                                            <input
                                                type="text"
                                                value={currentElement.bgColor || '#ffffff'}
                                                onChange={(e) => updateElementStyle(selectedElement, 'bgColor', e.target.value)}
                                                className="flex-1 px-3 py-2 border rounded font-mono text-sm"
                                                placeholder="#ffffff"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium text-gray-700 mb-2 block">
                                            Background Opacity
                                        </label>
                                        <div className="flex items-center gap-3">
                                            <input
                                                type="range"
                                                min="0"
                                                max="100"
                                                value={Math.round((currentElement.bgOpacity ?? 0.9) * 100)}
                                                onChange={(e) => updateElementStyle(selectedElement, 'bgOpacity', parseInt(e.target.value) / 100)}
                                                className="flex-1 h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer"
                                            />
                                            <span className="text-sm font-mono bg-white px-3 py-1 rounded border min-w-[60px] text-center">
                                                {Math.round((currentElement.bgOpacity ?? 0.9) * 100)}%
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Size Controls */}
                            {hasSize && (
                                <div className="bg-gray-50 rounded-xl p-5">
                                    <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        📏 Size
                                    </h3>
                                    <div className="flex items-center gap-3">
                                        <input
                                            type="range"
                                            min="50"
                                            max="300"
                                            value={currentElement.size || 100}
                                            onChange={(e) => updateElementStyle(selectedElement, 'size', parseInt(e.target.value))}
                                            className="flex-1 h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer"
                                        />
                                        <span className="text-sm font-mono bg-white px-3 py-1 rounded border min-w-[60px] text-center">
                                            {currentElement.size || 100}px
                                        </span>
                                    </div>
                                </div>
                            )}

                            {/* Visibility Toggle */}
                            <div className="bg-gray-50 rounded-xl p-5">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                                            <Eye className="w-5 h-5" />
                                            Visibility
                                        </h3>
                                        <p className="text-sm text-gray-600 mt-1">
                                            {globalVisibility[selectedElement] ? 'Currently visible' : 'Currently hidden'}
                                        </p>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={globalVisibility[selectedElement] || false}
                                            onChange={(e) => setGlobalVisibility(prev => ({
                                                ...prev,
                                                [selectedElement]: e.target.checked
                                            }))}
                                            className="sr-only peer"
                                        />
                                        <div className="w-14 h-7 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-blue-600"></div>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Panel - Preview */}
                    <div className="w-[500px] bg-gradient-to-br from-gray-100 to-gray-200 p-6 overflow-y-auto">
                        <div className="mb-4">
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="font-semibold text-gray-800">Live Preview</h3>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={handleDownloadPreview}
                                        className="px-3 py-1 bg-white border rounded text-sm hover:bg-gray-100 flex items-center gap-1"
                                        title="Download Preview"
                                    >
                                        <Download className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={() => setPreviewScale(Math.max(25, previewScale - 25))}
                                        className="px-2 py-1 bg-white border rounded text-sm"
                                    >
                                        -
                                    </button>
                                    <span className="text-sm font-medium min-w-[50px] text-center">{previewScale}%</span>
                                    <button
                                        onClick={() => setPreviewScale(Math.min(100, previewScale + 25))}
                                        className="px-2 py-1 bg-white border rounded text-sm"
                                    >
                                        +
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-center">
                            <div style={{ transform: `scale(${previewScale / 100})`, transformOrigin: 'top center' }}>
                                {/* ID Card Preview */}
                                <div
                                    ref={cardRef}
                                    className="relative rounded-[1px] h-[610px] w-[430px]"
                                    style={{
                                        backgroundImage: getBackground() ? `url(${getBackground()})` : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center'
                                    }}
                                >
                                    <div className="relative z-10 h-full text-white">
                                        {getBackground() && (
                                            <div className="absolute inset-0">
                                                <img
                                                    src={getBackground()}
                                                    alt=""
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>
                                        )}

                                        <div className="relative h-full flex flex-col justify-between p-4 items-center">
                                            {/* Render dynamic participant fields */}
                                            {event.participantFields?.map(field => {
                                                const fieldKey = field.fieldName.toLowerCase().replace(/\s+/g, '');
                                                const style = elementStyles[fieldKey];
                                                const visible = globalVisibility[fieldKey];
                                                const value = sampleParticipant.data[field.fieldName];

                                                if (!visible || !value || !style) return null;

                                                return (
                                                    <p
                                                        key={fieldKey}
                                                        style={{
                                                            position: 'absolute',
                                                            top: style.top ? `${style.top}px` : undefined,
                                                            bottom: style.bottom ? `${style.bottom}px` : undefined,
                                                            left: style.left || '50%',
                                                            right: style.right,
                                                            fontSize: `${style.fontSize || 16}px`,
                                                            color: style.color || 'black',
                                                            textAlign: style.textAlign || 'center',
                                                            transform: style.left === '50%' ? 'translateX(-50%)' :
                                                                style.left === '100%' ? 'translateX(-100%)' :
                                                                    undefined,
                                                            fontWeight: field.fieldName.toLowerCase().includes('name') ? 'bold' : 'normal',
                                                            maxWidth: '90%',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap'
                                                        }}
                                                    >
                                                        {value}
                                                    </p>
                                                );
                                            })}

                                            {/* QR Code */}
                                            {globalVisibility.qrCode && elementStyles.qrCode && (
                                                <div
                                                    style={{
                                                        position: 'absolute',
                                                        top: elementStyles.qrCode.top ? `${elementStyles.qrCode.top}px` : undefined,
                                                        bottom: elementStyles.qrCode.bottom ? `${elementStyles.qrCode.bottom}px` : undefined,
                                                        left: elementStyles.qrCode.left || '50%',
                                                        transform: elementStyles.qrCode.left === '50%' ? 'translateX(-50%)' :
                                                            elementStyles.qrCode.left === '100%' ? 'translateX(-100%)' :
                                                                undefined
                                                    }}
                                                    className="rounded-xl bg-white px-2 pb-3 shadow-md"
                                                >
                                                    <div style={{ maxWidth: elementStyles.qrCode.size }} className="mx-auto">
                                                        <p className="text-center text-[13px] text-black font-bold py-1 break-words">
                                                            Show at the Venue
                                                        </p>
                                                    </div>
                                                    <QRCode
                                                        value={`${window.location.origin}/checkin/${sampleParticipant._id}`}
                                                        size={elementStyles.qrCode.size || 100}
                                                        level="H"
                                                        className="rounded-lg mx-auto"
                                                    />
                                                </div>
                                            )}

                                            {/* Registration ID */}
                                            {globalVisibility.participantId && elementStyles.participantId && sampleParticipant.registrationId && (
                                                <p
                                                    style={{
                                                        position: 'absolute',
                                                        top: elementStyles.participantId.top ? `${elementStyles.participantId.top}px` : undefined,
                                                        bottom: elementStyles.participantId.bottom ? `${elementStyles.participantId.bottom}px` : undefined,
                                                        left: elementStyles.participantId.left || '50%',
                                                        fontSize: `${elementStyles.participantId.fontSize || 12}px`,
                                                        color: elementStyles.participantId.color || 'black',
                                                        textAlign: elementStyles.participantId.textAlign || 'center',
                                                        transform: elementStyles.participantId.left === '50%' ? 'translateX(-50%)' :
                                                            elementStyles.participantId.left === '100%' ? 'translateX(-100%)' :
                                                                undefined,
                                                        fontWeight: 'semibold'
                                                    }}
                                                >
                                                    ID: {sampleParticipant.registrationId}
                                                </p>
                                            )}

                                            {/* Participant Category (free events) */}
                                            {globalVisibility.categoryName && elementStyles.categoryName && sampleParticipant.categoryName && (() => {
                                                const st = elementStyles.categoryName;
                                                const hex = st.bgColor || '#ffffff';
                                                const opacity = st.bgOpacity ?? 0.9;
                                                // convert hex + opacity to rgba
                                                const r = parseInt(hex.slice(1,3),16);
                                                const g = parseInt(hex.slice(3,5),16);
                                                const b = parseInt(hex.slice(5,7),16);
                                                return (
                                                    <div
                                                        style={{
                                                            position: 'absolute',
                                                            top: st.top ? `${st.top}px` : undefined,
                                                            bottom: st.bottom ? `${st.bottom}px` : undefined,
                                                            left: st.left || '50%',
                                                            transform: st.left === '50%' ? 'translateX(-50%)' : st.left === '100%' ? 'translateX(-100%)' : undefined,
                                                            fontSize: `${st.fontSize || 16}px`,
                                                            color: st.color || 'black',
                                                            textAlign: st.textAlign || 'center',
                                                            fontWeight: 'bold',
                                                            lineHeight: '1',
                                                            padding: '6px',
                                                            borderRadius: '8px',
                                                            whiteSpace: 'nowrap',
                                                            backgroundColor: `rgba(${r},${g},${b},${opacity})`,
                                                        }}
                                                    >
                                                        {sampleParticipant.categoryName}
                                                    </div>
                                                );
                                            })()}

                                            {/* Ticket Category */}
                                            {globalVisibility.ticketCategory && elementStyles.ticketCategory && sampleParticipant.ticketCategory && (
                                                <div
                                                    style={{
                                                        position: 'absolute',
                                                        top: elementStyles.ticketCategory.top ? `${elementStyles.ticketCategory.top}px` : undefined,
                                                        bottom: elementStyles.ticketCategory.bottom ? `${elementStyles.ticketCategory.bottom}px` : undefined,
                                                        left: elementStyles.ticketCategory.left || '50%',
                                                        transform: elementStyles.ticketCategory.left === '50%' ? 'translateX(-50%)' : undefined,
                                                        fontSize: `${elementStyles.ticketCategory.fontSize || 16}px`,
                                                        color: elementStyles.ticketCategory.color || 'black',
                                                        padding: '4px 16px',
                                                        borderRadius: '8px',
                                                        fontWeight: 'bold'
                                                    }}
                                                    className="bg-white bg-opacity-90"
                                                >
                                                    {sampleParticipant.ticketCategory}
                                                </div>
                                            )}

                                            {/* Ticket Dates */}
                                            {globalVisibility.ticketDates && elementStyles.ticketDates && sampleParticipant.ticketDates && (
                                                <p
                                                    style={{
                                                        position: 'absolute',
                                                        top: elementStyles.ticketDates.top ? `${elementStyles.ticketDates.top}px` : undefined,
                                                        bottom: elementStyles.ticketDates.bottom ? `${elementStyles.ticketDates.bottom}px` : undefined,
                                                        left: elementStyles.ticketDates.left || '50%',
                                                        fontSize: `${elementStyles.ticketDates.fontSize || 14}px`,
                                                        color: elementStyles.ticketDates.color || 'black',
                                                        textAlign: elementStyles.ticketDates.textAlign || 'center',
                                                        transform: elementStyles.ticketDates.left === '50%' ? 'translateX(-50%)' : undefined,
                                                        fontWeight: 'normal'
                                                    }}
                                                >
                                                    {formatTicketDates(sampleParticipant.ticketDates)}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between p-6 border-t bg-gray-50">
                    <button
                        onClick={() => setShowVisibilityModal(true)}
                        className="flex items-center gap-2 px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    >
                        <Eye className="w-4 h-4" />
                        Visibility Settings
                    </button>
                    <div className="flex gap-3">
                        <button
                            onClick={onClose}
                            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSave}
                            className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all shadow-md"
                        >
                            <Save className="w-4 h-4" />
                            Save Changes
                        </button>
                    </div>
                </div>
            </div>

            {/* Visibility Modal */}
            {showVisibilityModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
                        <div className="p-6 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-t-2xl">
                            <h3 className="text-xl font-bold flex items-center gap-2">
                                <Eye className="w-5 h-5" />
                                Visibility Settings
                            </h3>
                            <p className="text-blue-100 text-sm mt-1">Toggle which fields appear on ID cards</p>
                        </div>
                        <div className="p-6 max-h-[60vh] overflow-y-auto">
                            <div className="space-y-3">
                                {/* Landscape Print toggle — controls Print SVG orientation */}
                                <div className="flex items-center justify-between p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                                    <div className="flex items-center gap-3">
                                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-xl ${globalVisibility.isLandscapePrint ? 'bg-indigo-100' : 'bg-gray-200'}`}>
                                            🖨️
                                        </div>
                                        <div>
                                            <div className="font-medium text-gray-800">Landscape Print (SVG)</div>
                                            <div className="text-xs text-gray-500">
                                                {globalVisibility.isLandscapePrint ? 'Print SVG rotated landscape' : 'Print SVG in portrait (default)'}
                                            </div>
                                        </div>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={globalVisibility.isLandscapePrint || false}
                                            onChange={(e) => setGlobalVisibility(prev => ({
                                                ...prev,
                                                isLandscapePrint: e.target.checked
                                            }))}
                                            className="sr-only peer"
                                        />
                                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                                    </label>
                                </div>

                                <div className="border-t border-gray-200 my-1"></div>

                                {elements.map(element => (
                                    <div key={element.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                                        <div className="flex items-center gap-3">
                                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-xl ${globalVisibility[element.id] ? 'bg-green-100' : 'bg-gray-200'
                                                }`}>
                                                {element.icon}
                                            </div>
                                            <div>
                                                <div className="font-medium text-gray-800">{element.label}</div>
                                                <div className="text-xs text-gray-500">
                                                    {globalVisibility[element.id] ? 'Currently visible' : 'Currently hidden'}
                                                </div>
                                            </div>
                                        </div>
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={globalVisibility[element.id] || false}
                                                onChange={(e) => setGlobalVisibility(prev => ({
                                                    ...prev,
                                                    [element.id]: e.target.checked
                                                }))}
                                                className="sr-only peer"
                                            />
                                            <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="flex justify-end gap-3 p-6 border-t">
                            <button
                                onClick={() => setShowVisibilityModal(false)}
                                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DesignCustomizationModal;
